import { LitElement, css, html} from 'lit';
import {property, customElement} from 'lit/decorators.js';

import { VoyageRoute, VoyageRoutingModel } from '../../../src/models/voyage-routing-model';
import { VesselPlanningModel } from '../../../src/models/vessel-planning-model'
import { SpotWeatherInfo, WeatherModel } from '../../../src/models/weather-model';
import '../../voyage-ship-weather-expectation/src/voyage-ship-weather-expectation';

import { toContext } from 'ol/render';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { LayersFactory } from '@maersk-global/map-layers';
import { SuggestedRouteEvents } from '../../voyage-suggested-routes/src/types';

const WEATHER_TIME_BUCKETS = 60*60*1000;
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

interface WeatherPoint
{
    timestamp: number;
    weather: SpotWeatherInfo;
}

export interface RouteWithWeather
{
    route: VoyageRoute;
    label: string;
    color: string;
    weatherByTimestamp: WeatherPoint[];
    currentIndex: number;
    id: string;
}

// Shows a ship weather expectation for each active route that contains weather data at the current player time.
@customElement('voyage-ship-wx-player')
export class VoyageShipWeatherExpectationPlayer extends LitElement {

    @property({type: String}) mode: string = "";

    constructor() {
        super();

        this.onRoutesChanged = this.onRoutesChanged.bind(this);
    }

    static get styles() {
        return css`
            voyage-ship-wx {
                margin-bottom: 10px;
            }

            .legendContainer {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                background-color: var(--neutrals-grey-10, white);
                height: 41px;
                width: 100%;
            }

            .legendContainer[hidden] {
                display: none;
            }

            canvas {
                height: 16px;
                width: 18px;
                margin-right: 5px;
            }

            .legend {
                display: flex;
                align-items: center;
                font-family: MaerskText;
                font-size: 12px;
                color: var(--neutrals-grey-03, black);
                margin-right: 10px;
                margin-left: 10px;
            }
        `;
    }

    render() {
        const filteredRoutes = this._routesWithWeather.filter(x => x.currentIndex !== -1);

        return html`
            ${filteredRoutes.map(routeWithWeather => html`
                <voyage-ship-wx
                    .heading=${this.headingAt(routeWithWeather.weatherByTimestamp[routeWithWeather.currentIndex].timestamp, routeWithWeather.route)}
                    .weatherElements=${routeWithWeather.weatherByTimestamp[routeWithWeather.currentIndex].weather}
                    routeName=${routeWithWeather.label} routeColor=${routeWithWeather.color}></voyage-ship-wx>
            `)}
            <div class="legendContainer" ?hidden=${filteredRoutes.length === 0}>
                <div class="legend">
                    <canvas id="swellCanvas" height="16" width="18"></canvas>
                    Swell
                </div>
                <div class="legend">
                    <canvas id="waveCanvas" height="16" width="18"></canvas>
                    Sea
                </div>
                <div class="legend">
                    <canvas id="windCanvas" height="16" width="18"></canvas>
                    Wind
                </div>
                <div class="legend">
                    <canvas id="currentCanvas" height="16" width="18"></canvas>
                    Current
                </div>
            </div>
        `;
    }

    set currentTime(value) {
        this._currentTime = value;
        
        let dirty = false;
        for (let routeWithWeather of this._routesWithWeather) {
            let currentIndex = -1;
            for (let i = 0; i < routeWithWeather.weatherByTimestamp.length; i++) {
                if (routeWithWeather.weatherByTimestamp[i].timestamp < this._currentTime) {
                    currentIndex = i;
                }
            }

            if (currentIndex !== routeWithWeather.currentIndex) {
                routeWithWeather.currentIndex = currentIndex;
                dirty = true;
            }
        }

        if (dirty) {
            this.requestUpdate();
        }
    }

    @property({type: Number})
    get currentTime() {
        return this._currentTime;
    }

    firstUpdated() {
        VoyageRoutingModel!.addEventListener(SuggestedRouteEvents.RoutesChanged, this.onRoutesChanged);
        VesselPlanningModel!.addEventListener("executing-plan-changed", this.onRoutesChanged);
        this.onRoutesChanged();

        this._drawLegends();
    }

    disconnectedCallback() {
        VoyageRoutingModel!.removeEventListener(SuggestedRouteEvents.RoutesChanged, this.onRoutesChanged);
        VesselPlanningModel!.removeEventListener("executing-plan-changed", this.onRoutesChanged);
    }

    async onRoutesChanged() {
        const styles = getComputedStyle(this);
        const colors = [
            styles.getPropertyValue('--route1-color') || 'green',
            styles.getPropertyValue('--route2-color') || 'orange',
            styles.getPropertyValue('--route3-color') || 'purple',
            styles.getPropertyValue('--neutrals-grey-01') || 'black'
        ];

        this._routesWithWeather = [];
        const promises = [];
        const uiRoutes = VoyageRoutingModel!.uiRoutes;
        promises.push(this.gatherRouteWeather(uiRoutes[0].route, "Route 1", colors[0], "route1"));
        promises.push(this.gatherRouteWeather(uiRoutes[1].route, "Route 2", colors[1], "route2"));
        promises.push(this.gatherRouteWeather(uiRoutes[2].route, "Route 3", colors[2], "route3"));

        if (this.mode === "schedule") {
            const plan = await VesselPlanningModel!.getExecutingPlan();
            if (plan && plan.selectedRouteIndex > -1) {
                promises.push(this.gatherRouteWeather(plan.routes[plan.selectedRouteIndex], "Route", colors[3], "executingRoute"));
            }
        }
        
        await Promise.all(promises);
        this.requestUpdate()
    }

    async gatherRouteWeather(route: VoyageRoute|null, label: string, color: string, id: string) {
        if (!route || !route.route_info) return;

        const etd = Math.max(new Date(route.route_info.etd).getTime(), Date.now());
        const eta = new Date(route.route_info.eta).getTime();

        // Playback is not possible for routes in the past
        if (eta < Date.now()) return;

        // Compute relevant times and get ship positions
        const timestamps = [ this.getTimeBucket(etd) ];
        while (timestamps[timestamps.length-1] + WEATHER_TIME_BUCKETS < eta)
        {
            timestamps.push(timestamps[timestamps.length-1] + WEATHER_TIME_BUCKETS);
        }

        const locationAndTimes = [];

        for (const timestamp of timestamps)
        {
            const positionAndHeading = VoyageRoutingModel!.getPositionAndHeadingInTime(timestamp, route);
            if (positionAndHeading) {
                locationAndTimes.push({
                    timestamp: new Date(timestamp).toISOString(),
                    coordinates: positionAndHeading.position
                });
            }
        }

        // Gather weather information
        const weatherByTimestamp: any = [];
        let currentIndex = -1;

        if (isVesselOnCloud) {
            route.route_geometry.forEach(node => {
                if (node.properties.hasOwnProperty('spotWeathers')) {
                    node.properties.spotWeathers.forEach((spot: any) => {
                        const timestamp = new Date(<string>spot.timestamp).getTime();
                        let weather: any[] = [];
                        const weatherObj = spot.weather;
                        for (const key in weatherObj) {
                            if (weatherObj.hasOwnProperty(key)) {
                                const item = weatherObj[key];
                                weather.push({
                                    element: item.element,
                                    value: item.direction ? [item.direction, item.value] : [item.value], 
                                })
                            }
                        }
                        weatherByTimestamp.push({
                            timestamp,
                            weather,
                        });

                        if (timestamp < this.currentTime) {
                            currentIndex = weatherByTimestamp.length - 1;
                        }
                    });
                }
            });
        } else {
            const weatherPoints = await WeatherModel!.getMultiSpotWeather(locationAndTimes);

            for (const weatherPoint of weatherPoints)
            {
                
                const timestamp = new Date(<string>weatherPoint.properties.timestamp).getTime();
                weatherByTimestamp.push({
                    timestamp,
                    weather: weatherPoint.properties.weather
                });

                if (timestamp < this.currentTime) {
                    currentIndex = weatherByTimestamp.length - 1;
                }
            }
       }
        if (!weatherByTimestamp.length) return;

        this._routesWithWeather.push({
            route,
            label,
            color,
            weatherByTimestamp,
            currentIndex,
            id
        });
    }

    getTimeBucket(timestamp: number) {
        return Math.floor(timestamp / WEATHER_TIME_BUCKETS) * WEATHER_TIME_BUCKETS;
    }

    headingAt(timestamp: number, route: VoyageRoute) {
        const positionAndHeading = VoyageRoutingModel!.getPositionAndHeadingInTime(timestamp, route);
        if (!positionAndHeading) return 0;
        return positionAndHeading.heading * 180 / Math.PI;
    }

    protected _currentTime: number = Date.now();
    protected _routesWithWeather: RouteWithWeather[] = [];

    protected _drawLegends() {
        const stylesFactory = new LayersFactory({
            chartWMSUrl: '',
            weatherWMSUrl: '',
            weatherBorderWMSUrl: '',
            weatherNonGridUrl: '',
            player: <EventTarget><unknown>null // HACK: The player won't actually get used in this case, so null is OK
        }).styles;

        // Note: The values used below for drawing the (graphical part of) legends depend on the internal workings of the MG layers
        // (more specifically, the sizing and positioning of the drawed feature is assumed to work a certain way)
        // - if this changes it may be neccessary to update the values used

        const initCanvas = (canvas: HTMLCanvasElement) => {
            // Clear the canvas and create an OpenLayers canvas context
            const context = canvas.getContext('2d')!;
            context.clearRect(0, 0, canvas.width, canvas.height);        
            return toContext(context);
        }

        let olRender = initCanvas(<HTMLCanvasElement>this.shadowRoot!.getElementById("swellCanvas"));

        // Swell is an arrowGeometry with sizeFactor 10
        const swellFeature = new Feature({
            geometry: new Point([17,8]),
            Value: 5.3096,
            Direction: 270
        });
        const swellStyle = stylesFactory.getStyle({element: 'Swell', view: 'grid'})(swellFeature, 1);
        swellStyle.getStroke().setWidth(2);
        olRender.drawFeature(swellFeature, swellStyle);

        olRender = initCanvas(<HTMLCanvasElement>this.shadowRoot!.getElementById("waveCanvas"));

        // Wave (aka Sea) is an arrowGeometry with sizeFactor 10
        const seaFeature = new Feature({
            geometry: new Point([17,8]),
            Value: 5.3096,
            Direction: 270
        });
        const seaStyle = stylesFactory.getStyle({element: 'Wave', view: 'grid'})(seaFeature, 1);
        seaStyle.getStroke().setWidth(2);
        olRender.drawFeature(seaFeature, seaStyle);

        olRender = initCanvas(<HTMLCanvasElement>this.shadowRoot!.getElementById("windCanvas"));

        // Wind10M is a windGeometry with sizeFactor 10
        const windFeature = new Feature({
            geometry: new Point([25,8]),
            Value: 10,
            Direction: 270
        });
        const windStyle = stylesFactory.getStyle({element: 'Wind10M', view: 'grid', additionalOptions: {unit: 'mps'}})(windFeature, 0.8);
        windStyle.getStroke().setWidth(2);
        olRender.drawFeature(windFeature, windStyle);

        olRender = initCanvas(<HTMLCanvasElement>this.shadowRoot!.getElementById("currentCanvas"));

        // Current is an arrowGeometry with sizeFactor 30
        const currentFeature = new Feature({
            geometry: new Point([17,8]),
            Value: 0.8479,
            Direction: 270
        });
        const currentStyle = stylesFactory.getStyle({element: 'Currents', view: 'grid'})(currentFeature, 1);
        currentStyle.getStroke().setWidth(2);
        olRender.drawFeature(currentFeature, currentStyle);
    }
}
